import { Suspense, lazy } from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router';

import { OidcSecure } from '@axa-fr/react-oidc';
import { relativeRoutes, routes } from '@fly/shared';

const SignUp = lazy(() => import('./SignUp'));
const UserInfo = lazy(() => import('./containers/UserInfo/UserInfo'));
const ProvidersSelect = lazy(
  () => import('./containers/ProvidersSelect/ProvidersSelect')
);
const EmailPassword = lazy(
  () => import('./containers/EmailPassword/EmailPassword')
);
const VerificationCode = lazy(
  () => import('./containers/VerificationCode/VerificationCode')
);
const OAuthCallback = lazy(
  () => import('./containers/OAuthCallback/OAuthCallback')
);
const ConfirmInvitation = lazy(() => import('./containers/ConfirmInvitation'));
const ConfirmInvitationGuard = lazy(
  () => import('./guards/ConfirmInvitationGuard')
);

const AuthRedirectGuard = lazy(() => import('./guards/AuthRedirectGuard'));
const DesktopSignupRedirect = lazy(
  () => import('./guards/DesktopSignupRedirect')
);
const UserInfoGuard = lazy(() => import('./guards/UserInfoGuard'));
const TeamRegisterGuard = lazy(() => import('./guards/TeamRegisterGuard'));
const VerificationCodeGuard = lazy(
  () => import('./guards/VerificationCodeGuard')
);
const SignupFunnelProvider = lazy(() => import('./SignupFunnelContext'));

export const signUpRoutes: RouteObject[] = [
  {
    path: `${routes.SIGN_UP.ROOT}/*`,
    element: (
      <Suspense>
        <SignupFunnelProvider>
          <SignUp />
        </SignupFunnelProvider>
      </Suspense>
    ),
    children: [
      {
        path: `${relativeRoutes.SIGN_UP_DESKTOP}`,
        element: (
          <Suspense>
            <DesktopSignupRedirect />
          </Suspense>
        ),
      },
      {
        path: `${relativeRoutes.TEAM_REGISTER}`,
        element: (
          <Suspense>
            <OidcSecure>
              <TeamRegisterGuard>
                <UserInfo />
              </TeamRegisterGuard>
            </OidcSecure>
          </Suspense>
        ),
      },
      {
        element: (
          <Suspense>
            <AuthRedirectGuard>
              <Outlet />
            </AuthRedirectGuard>
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={routes.SIGN_UP.PROVIDERS} replace />,
          },
          {
            path: `${relativeRoutes.PROVIDERS}`,
            element: (
              <Suspense>
                <ProvidersSelect />
              </Suspense>
            ),
          },
          {
            path: relativeRoutes.CONFIRM_INVITATION,
            element: (
              <Suspense>
                <ConfirmInvitationGuard>
                  <ConfirmInvitation />
                </ConfirmInvitationGuard>
              </Suspense>
            ),
          },
          {
            path: `${relativeRoutes.USER_INFO}`,
            element: (
              <Suspense>
                <UserInfoGuard>
                  <UserInfo />
                </UserInfoGuard>
              </Suspense>
            ),
          },
          {
            path: `${relativeRoutes.EMAIL_PASSWORD}`,
            element: (
              <Suspense>
                <EmailPassword />
              </Suspense>
            ),
          },
          {
            path: `${relativeRoutes.VERIFICATION_CODE}`,
            element: (
              <Suspense>
                <VerificationCodeGuard>
                  <VerificationCode />
                </VerificationCodeGuard>
              </Suspense>
            ),
          },
          {
            path: `${relativeRoutes.OAUTH_CALLBACK}`,
            element: (
              <Suspense>
                <OAuthCallback />
              </Suspense>
            ),
          },
          {
            path: '*',
            element: <Navigate to={routes.SIGN_UP.PROVIDERS} />,
          },
        ],
      },
    ],
  },
];
